import { SortField } from 'lib/services/content-service';
import { useContentQuery } from './ContentQueryContext';
import styles from './Pagination.module.scss';
import { Item } from '@sitecore-content-sdk/nextjs';
import { useEffect, useRef, useState } from 'react';
import useDictionary from 'lib/hooks/useDictionary';
import {
  pushSearchSortEvent,
  SearchAnalyticsType,
} from 'components/helpers/Analytics/SearchAndFilters/SearchAndFiltersAnalytics';
import { DEFAULT_CONTENT_PAGE_SIZE } from '../CardListingShared';

export const Sorting = ({
  sortByOptions,
  showResultCount,
  showSorting,
  onSortChangeStart,
  searchType = 'Standard',
  searchAnalyticsEnabled = false,
}: {
  sortByOptions?: Item[];
  showResultCount?: boolean;
  showSorting?: boolean;
  onSortChangeStart?: () => void;
  searchType?: SearchAnalyticsType;
  searchAnalyticsEnabled?: boolean;
}) => {
  const { query, searchResponse, setQuery } = useContentQuery();
  const [isSticky, setIsSticky] = useState(false);
  const sortingRef = useRef<HTMLDivElement | null>(null);

  const totalItems = searchResponse?.Count ?? 0;
  const itemsPerPage = searchResponse?.PageSize ?? DEFAULT_CONTENT_PAGE_SIZE;
  const currentPage = searchResponse?.PageIndex ?? 1;
  const { getDictionaryValue } = useDictionary();
  const sortByLabel = getDictionaryValue('Components.AdvancedContentGrid.SortBy');

  const toOption = (item: Item) => {
    // Try to get fields from item.fields or item.jsonValue?.fields
    const fields =
      item.fields ??
      (typeof item === 'object' &&
        'jsonValue' in item &&
        typeof (item as { jsonValue?: unknown }).jsonValue === 'object' &&
        (item as { jsonValue?: { fields?: unknown } }).jsonValue?.fields) ??
      {};

    // Helper to extract value from possible field types
    const getFieldValue = (field: unknown): string | undefined => {
      if (typeof field === 'object' && field !== null && 'value' in field) {
        return (field as { value: string }).value;
      }
      if (typeof field === 'string') {
        return field;
      }
      return undefined;
    };

    // Value: try Value, value, fallback to item.name or ''
    const value =
      getFieldValue((fields as Record<string, unknown>)?.Value) ??
      getFieldValue((fields as Record<string, unknown>)?.value) ??
      (typeof item === 'object' &&
      'value' in item &&
      typeof (item as { value?: unknown }).value === 'string'
        ? (item as { value?: string }).value
        : undefined) ??
      item.name ??
      '';

    // Label: try Text, text, fallback to item.name or value
    const label =
      getFieldValue((fields as Record<string, unknown>)?.Text) ??
      getFieldValue((fields as Record<string, unknown>)?.text) ??
      (typeof item === 'object' &&
      'text' in item &&
      typeof (item as { text?: unknown }).text === 'string'
        ? (item as { text?: string }).text
        : undefined) ??
      item.name ??
      value;

    return { value, label };
  };

  const sortByList = sortByOptions && sortByOptions.length > 0 ? sortByOptions.map(toOption) : [];

  const applySort = (sortBy: SortField) => {
    if (setQuery) {
      if (searchAnalyticsEnabled) {
        pushSearchSortEvent({
          searchType,
          siteSearchPhrase: query?.searchTerm ?? '',
          searchSortType: sortBy,
        });
      }
      onSortChangeStart?.();
      setQuery({ ...(query ?? {}), sortBy, sortDirection: '', pageIndex: 1 });
    }
  };

  useEffect(() => {
    const updateStickyState = () => {
      const isDesktop = window.matchMedia('(min-width: 75rem)').matches;

      if (!isDesktop || !sortingRef.current) {
        setIsSticky(false);
        return;
      }

      setIsSticky(sortingRef.current.getBoundingClientRect().top <= 0);
    };

    updateStickyState();
    window.addEventListener('scroll', updateStickyState, { passive: true });
    window.addEventListener('resize', updateStickyState);

    return () => {
      window.removeEventListener('scroll', updateStickyState);
      window.removeEventListener('resize', updateStickyState);
    };
  }, []);

  return (
    <div
      ref={sortingRef}
      className={`rte ${styles.sorting} ${isSticky ? styles.sortingSticky : ''}`.trim()}
    >
      {showResultCount && totalItems > 0 && (
        <div className={styles.paginationInfo}>
          <span>
            {currentPage * itemsPerPage - itemsPerPage + 1} -{' '}
            {Math.min(currentPage * itemsPerPage, totalItems)}
          </span>{' '}
          {getDictionaryValue('Components.AdvancedContentGrid.PageInfo')} {totalItems}
        </div>
      )}
      {showSorting && sortByList.length > 0 && (
        <div className={styles.sortingControls}>
          <label htmlFor="content-grid-sortby" className="screenReaderText">
            {sortByLabel}
          </label>
          <span aria-hidden="true" className={styles.sortingLabel}>
            <b>{sortByLabel}</b>
          </span>
          <select
            id="content-grid-sortby"
            className={styles.select}
            value={(query?.sortBy as string) ?? 'None'}
            onChange={(e) => {
              const newSort = e.target.value as SortField;
              applySort(newSort);
            }}
          >
            {sortByList.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};
